import { toast } from '@/components/toast';
import { useAnalytics } from '@/services/analytics';
import { getRecaptchaScore, getRecaptchaSiteKey } from '@/services/api';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import styled from '@emotion/styled';
import React, { MouseEvent, ReactElement } from 'react';

const RECAPTCHA_DEFAULT_THRESHOLD = 0.1;

const validateRecaptcha = async (
    minScore: number
): Promise<{
    isValid: boolean;
    error?: string;
    score?: number;
}> => {
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) {
        return { isValid: true };
    }

    try {
        const siteKey = await getRecaptchaSiteKey();
        const token = await grecaptcha.execute(siteKey, { action: 'submit' });

        if (!token) {
            return { isValid: false, error: 'Recaptcha token is empty' };
        }

        const score = await getRecaptchaScore(token);

        const isValid = !!score && score > minScore;

        return { isValid, error: isValid ? undefined : `Recaptcha score is too low`, score };
    } catch (error) {
        return { isValid: false, error: `Recaptcha validation failed` };
    }
};

interface ClickableProps {
    onClick?: (event: MouseEvent<any>) => void;
    isLoading?: boolean;
}

const DisclaimerText = styled.p`
    font-size: 1rem;
    margin-top: 2px;
    color: grey;
    a {
        color: blue;
        text-decoration: underline;
        &:hover {
            color: blue;
        }
    }
`;
const Disclaimer = () => {
    return (
        <DisclaimerText role="contentinfo" aria-label="reCAPTCHA disclaimer">
            This site is protected by reCAPTCHA and the Google
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {' '}
                Privacy Policy
            </a>{' '}
            and
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                {' '}
                Terms of Service
            </a>{' '}
            apply.
        </DisclaimerText>
    );
};

interface RecaptchaWrapperProps {
    minScore?: number;
    children: ReactElement<ClickableProps>;
    withDisclaimer?: boolean;
    source: string;
}

export const RecaptchaWrapper: React.FC<RecaptchaWrapperProps> = ({ minScore: minScoreProp, children, withDisclaimer, source }) => {
    const captchaEnabled = useFlag('recaptcha_enabled', false);
    const captchaMinScoreFF = useFlag('recaptcha_default_min_score', RECAPTCHA_DEFAULT_THRESHOLD);
    const [isLoading, setIsLoading] = React.useState(false);
    const mixpanel = useAnalytics();

    const minScore = minScoreProp ?? captchaMinScoreFF;

    const handleClick = async (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();

        try {
            setIsLoading(true);
            const { isValid, error, score } = await validateRecaptcha(minScore);
            if (!isValid) {
                toast.error({ title: 'Try again later' });
                logger.error('Recaptcha validation failed', { error, source });
                mixpanel?.track('recaptcha-failed', {
                    source,
                    error,
                    score,
                    minScore,
                });
                setIsLoading(false);
                return;
            }

            if (children.props.onClick) {
                children.props.onClick(event);
            }
        } catch (error) {
            toast.error({ title: 'Try again later' });
        } finally {
            setIsLoading(false);
        }
    };

    if (!captchaEnabled) {
        return <>{children}</>;
    }

    return (
        <>
            {React.cloneElement(children, {
                onClick: handleClick,
                isLoading,
            })}
            {withDisclaimer && <Disclaimer />}
        </>
    );
};
